import React, { useEffect, useState, useMemo } from "react";
import axiosInstance from "../../../../axios";
import { Loading } from "../../../Other/Loading";
import Pagination from "./Pagination";
import moment from "moment";
import {
  Trophy, CheckCircle2, XCircle, Calendar, Hash,
  BarChart2, Search, Target, TrendingUp, Award, Inbox,
  RefreshCw, FileSpreadsheet, ArrowUpDown, BookOpen
} from "lucide-react";
import Modal from "../../../Other/Modal";

const AdminQuizResults = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "createdOn", direction: "desc" });
  const [exporting, setExporting] = useState(false);

  // Review Answers Modal States
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);
  const [reviewError, setReviewError] = useState(null);

  const handleOpenReview = async (testId) => {
    setSelectedTestId(testId);
    setIsReviewModalOpen(true);
    setLoadingReview(true);
    setReviewError(null);
    setReviewData(null);
    try {
      const res = await axiosInstance.get(`/test/detail/${testId}`);
      setReviewData(res?.data?.data || null);
    } catch (err) {
      console.error("Error fetching test detail:", err);
      setReviewError("Failed to load review details. Please try again.");
    } finally {
      setLoadingReview(false);
    }
  };

  const handleCloseReview = () => {
    setIsReviewModalOpen(false);
    setSelectedTestId(null);
    setReviewData(null);
    setReviewError(null);
  };

  // Fetch all quiz results for admin on mount
  const fetchAllQuizResults = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch up to 10,000 records at once to allow dynamic client-side filtering, sorting and stats
      const res = await axiosInstance.get("/test/list?perPage=10000");
      setResults(res?.data?.data?.data || []);
    } catch (err) {
      console.error("Error fetching quiz results:", err);
      setError("Failed to retrieve quiz results. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllQuizResults();
  }, []);

  // Compute unique courses for filter dropdown
  const uniqueCourses = useMemo(() => {
    const courses = results.map(r => r.CourseName || "General Quiz");
    return ["All", ...new Set(courses)];
  }, [results]);

  // Handle client-side sorting and filtering
  const filteredResults = useMemo(() => {
    return results.filter(item => {
      const studentName = (item.StudentName || "").toLowerCase();
      const courseName = (item.CourseName || "General Quiz").toLowerCase();
      const quizName = (item.name || "Untitled Quiz").toLowerCase();
      const query = searchQuery.toLowerCase();

      // Search match
      const matchesSearch =
        studentName.includes(query) ||
        courseName.includes(query) ||
        quizName.includes(query);

      // Course match
      const matchesCourse =
        selectedCourse === "All" ||
        (item.CourseName || "General Quiz") === selectedCourse;

      // Status match
      const totalQ = item.rightAns + item.wrongAns;
      const pct = totalQ > 0 ? Math.round((item.rightAns / totalQ) * 100) : 0;
      const passed = pct >= 70;
      const matchesStatus =
        selectedStatus === "All" ||
        (selectedStatus === "Passed" && passed) ||
        (selectedStatus === "Failed" && !passed);

      return matchesSearch && matchesCourse && matchesStatus;
    });
  }, [results, searchQuery, selectedCourse, selectedStatus]);

  // Compute dynamic stats based on filtered results
  const stats = useMemo(() => {
    const total = filteredResults.length;
    if (total === 0) {
      return { total: 0, avgScore: 0, passRate: 0, passedCount: 0 };
    }

    let totalScoreSum = 0;
    let passedCount = 0;

    filteredResults.forEach(item => {
      const totalQ = item.rightAns + item.wrongAns;
      const pct = totalQ > 0 ? Math.round((item.rightAns / totalQ) * 100) : 0;
      totalScoreSum += pct;
      if (pct >= 70) {
        passedCount++;
      }
    });

    return {
      total,
      avgScore: Math.round(totalScoreSum / total),
      passRate: Math.round((passedCount / total) * 100),
      passedCount,
    };
  }, [filteredResults]);

  // Handle sort config
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredResults;

    return [...filteredResults].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Custom calculation for computed fields
      if (sortConfig.key === "score") {
        const totalA = a.rightAns + a.wrongAns || 1;
        const totalB = b.rightAns + b.wrongAns || 1;
        aValue = (a.rightAns / totalA) * 100;
        bValue = (b.rightAns / totalB) * 100;
      }

      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Default sorting for dates and strings
      return sortConfig.direction === "asc"
        ? new Date(aValue) - new Date(bValue)
        : new Date(bValue) - new Date(aValue);
    });
  }, [filteredResults, sortConfig]);

  // Handle pagination slicing
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const getScoreColors = (pct) => {
    // Elegant slate grayscale progress bar fill with clean semantic status badges
    if (pct >= 70) {
      return {
        text: "text-slate-700",
        bg: "bg-slate-700",
        badge: "bg-emerald-50 text-emerald-700 border-emerald-200/50",
      };
    }
    if (pct >= 50) {
      return {
        text: "text-slate-600",
        bg: "bg-slate-500",
        badge: "bg-amber-50 text-amber-700 border-amber-200/50",
      };
    }
    return {
      text: "text-slate-500",
      bg: "bg-slate-400",
      badge: "bg-rose-50 text-rose-700 border-rose-200/50",
    };
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCourse, selectedStatus]);

  // Export report to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const headers = [
        "Student Name",
        "Course",
        "Quiz Name",
        "Correct Answers",
        "Wrong Answers",
        "Score",
        "Result",
        "Date",
        "Instructor"
      ];

      const csvRows = sortedData.map(ele => {
        const totalQ = ele.rightAns + ele.wrongAns;
        const pct = totalQ > 0 ? Math.round((ele.rightAns / totalQ) * 100) : 0;
        const status = pct >= 70 ? "PASSED" : "FAILED";
        return [
          ele.StudentName || "Student",
          ele.CourseName || "General Quiz",
          ele.name || "Untitled Quiz",
          ele.rightAns,
          ele.wrongAns,
          `${pct}%`,
          status,
          ele.createdOn ? moment(ele.createdOn).format("YYYY-MM-DD HH:mm") : "",
          ele.InstructorName || ""
        ];
      });

      const csvContent = [headers, ...csvRows]
        .map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Quiz_Results_Report_${moment().format("YYYY-MM-DD")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("CSV Export error:", err);
    } finally {
      setExporting(false);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCourse("All");
    setSelectedStatus("All");
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={13} className="text-gray-400 opacity-60 ml-1 inline" />;
    }
    return sortConfig.direction === "asc" ? (
      <span className="text-slate-800 ml-1">▲</span>
    ) : (
      <span className="text-slate-800 ml-1">▼</span>
    );
  };

  return (
    <div className="w-full bg-white min-h-screen p-6 md:p-8">
      {/* Upper Title Block */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-2">
            <Trophy className="text-slate-600 w-8 h-8" />
            Quiz Performance Analytics
          </h1>
          <p className="text-slate-500 text-sm font-medium mt-1">
            Analyze, filter, and track quiz achievements across all courses and students.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={fetchAllQuizResults}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 transition-all font-bold text-sm shadow-sm active:scale-95 disabled:opacity-50"
            title="Refresh Data"
          >
            <RefreshCw size={16} className={`${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>

          <button
            onClick={handleExportCSV}
            disabled={loading || sortedData.length === 0 || exporting}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-bold text-sm shadow-sm transition-all active:scale-95 disabled:opacity-50"
            title="Export to CSV"
          >
            <FileSpreadsheet size={16} />
            {exporting ? "Exporting..." : "Export Report"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <Loading />
          <p className="text-slate-400 font-bold text-sm mt-4 animate-pulse">
            Loading performance analytics database...
          </p>
        </div>
      ) : error ? (
        <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 text-center max-w-2xl mx-auto shadow-sm my-10">
          <XCircle className="text-slate-400 w-16 h-16 mx-auto mb-4" />
          <h3 className="text-xl font-black text-slate-800 tracking-tight">Database Fetch Error</h3>
          <p className="text-slate-600 text-sm font-medium mt-2 leading-relaxed">{error}</p>
          <button
            onClick={fetchAllQuizResults}
            className="mt-6 bg-slate-800 text-white hover:bg-slate-900 px-6 py-3 rounded-xl font-bold text-sm uppercase transition-all shadow-md active:scale-95"
          >
            Try Reconnecting
          </button>
        </div>
      ) : (
        <>
          {/* Dashboard Summary Statistics Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow transition-all p-6 relative overflow-hidden group">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-slate-100 text-slate-600 rounded-xl">
                  <Hash size={22} />
                </div>
              </div>
              <p className="text-slate-400 text-xs font-extrabold uppercase tracking-widest">Total Attempts</p>
              <h3 className="text-3xl font-black text-slate-800 leading-tight mt-1">{stats.total}</h3>
              <p className="text-slate-400 text-[11px] font-semibold mt-1">Quiz attempts evaluated</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow transition-all p-6 relative overflow-hidden group">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-slate-100 text-slate-600 rounded-xl">
                  <Award size={22} />
                </div>
                <div className="text-right">
                  <span className="text-xl font-black text-slate-700">{stats.passRate}%</span>
                </div>
              </div>
              <p className="text-slate-400 text-xs font-extrabold uppercase tracking-widest">Average Pass Rate</p>
              <h3 className="text-3xl font-black text-slate-800 leading-tight mt-1">{stats.passedCount} <span className="text-xs font-medium text-slate-400">passed</span></h3>
              <p className="text-slate-400 text-[11px] font-semibold mt-1">Passing score is 70%+</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow transition-all p-6 relative overflow-hidden group">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-slate-100 text-slate-600 rounded-xl">
                  <BarChart2 size={22} />
                </div>
              </div>
              <p className="text-slate-400 text-xs font-extrabold uppercase tracking-widest">Average Score</p>
              <h3 className="text-3xl font-black text-slate-800 leading-tight mt-1">{stats.avgScore}%</h3>
              <p className="text-slate-400 text-[11px] font-semibold mt-1">Mean performance rating</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow transition-all p-6 relative overflow-hidden group">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-slate-100 text-slate-600 rounded-xl">
                  <TrendingUp size={22} />
                </div>
              </div>
              <p className="text-slate-400 text-xs font-extrabold uppercase tracking-widest">Average Failure Rate</p>
              <h3 className="text-3xl font-black text-slate-800 leading-tight mt-1">{stats.total - stats.passedCount} <span className="text-xs font-medium text-slate-400">failed</span></h3>
              <p className="text-slate-400 text-[11px] font-semibold mt-1">{100 - stats.passRate}% failed attempts</p>
            </div>
          </div>

          {/* Filtering Control Panel */}
          <div className="bg-slate-50/50 rounded-2xl border border-slate-200/85 p-5 mb-6 flex flex-col gap-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Search Bar */}
              <div className="relative w-full md:flex-1 max-w-lg">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4.5 h-4.5" />
                <input
                  type="text"
                  placeholder="Search by student name, course, or quiz..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500/10 focus:border-slate-500 transition-all shadow-sm"
                />
              </div>

              {/* Filters dropdown */}
              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
                <div className="flex flex-col">
                  <select
                    value={selectedCourse}
                    onChange={(e) => setSelectedCourse(e.target.value)}
                    className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500/10 focus:border-slate-500 transition-all shadow-sm"
                  >
                    <option value="All">All Courses</option>
                    {uniqueCourses.filter(c => c !== "All").map((c, i) => (
                      <option key={i} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500/10 focus:border-slate-500 transition-all shadow-sm"
                  >
                    <option value="All">All Statuses</option>
                    <option value="Passed">Passed (70%+)</option>
                    <option value="Failed">Failed (Under 70%)</option>
                  </select>
                </div>

                <div className="flex flex-col">
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500/10 focus:border-slate-500 transition-all shadow-sm"
                  >
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>

                {(searchQuery || selectedCourse !== "All" || selectedStatus !== "All") && (
                  <button
                    onClick={handleClearFilters}
                    className="px-4 py-2.5 bg-slate-100 text-slate-600 border border-slate-200 rounded-xl text-sm font-bold hover:bg-slate-200/60 transition-all duration-300 animate-in fade-in zoom-in-95"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Results count header */}
            <div className="flex justify-between items-center border-t border-slate-200/60 pt-3">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                {sortedData.length} records found {filteredResults.length !== results.length && "(filtered)"}
              </span>
              <span className="text-xs font-bold text-slate-400">
                Page {currentPage} of {totalPages || 1}
              </span>
            </div>
          </div>

          {/* Results Table Section */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
            {paginatedData.length === 0 ? (
              <div className="py-16 flex flex-col items-center justify-center text-center">
                <div className="bg-slate-50 w-20 h-20 rounded-3xl flex items-center justify-center mb-5 border border-slate-200/60">
                  <Inbox size={36} className="text-slate-400" />
                </div>
                <h4 className="text-xl font-bold text-slate-700 mb-1.5">No Quiz Results Found</h4>
                <p className="text-slate-400 text-sm font-medium max-w-sm leading-relaxed px-4">
                  We couldn't find any quiz attempts matching your search or filters. Try adjusting your query or click Refresh.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-fixed text-left border-collapse">
                  <thead className="bg-[#c7e1f4]">
                    <tr className="bg-slate-50 border-b border-slate-200/80 text-xs font-bold text-slate-500 uppercase tracking-widest select-none py-5">
                      <th className="w-[5%] py-4 px-3 text-center">S.No</th>
                      <th
                        onClick={() => handleSort("StudentName")}
                        className="w-[16%] py-4 px-3 cursor-pointer hover:bg-slate-200 transition-colors"
                      >
                        Student Name {renderSortIndicator("StudentName")}
                      </th>
                      <th
                        onClick={() => handleSort("CourseName")}
                        className="w-[13%] py-4 px-3 cursor-pointer hover:bg-slate-200 transition-colors"
                      >
                        Course {renderSortIndicator("CourseName")}
                      </th>
                      <th
                        onClick={() => handleSort("name")}
                        className="w-[13%] py-4 px-3 cursor-pointer hover:bg-slate-200 transition-colors"
                      >
                        Quiz Name {renderSortIndicator("name")}
                      </th>
                      <th
                        onClick={() => handleSort("score")}
                        className="w-[16%] py-4 px-3 cursor-pointer hover:bg-slate-200 transition-colors"
                      >
                        Performance Score {renderSortIndicator("score")}
                      </th>
                      <th
                        onClick={() => handleSort("createdOn")}
                        className="w-[11%] py-4 px-3 cursor-pointer hover:bg-slate-200 transition-colors"
                      >
                        Attempt Date {renderSortIndicator("createdOn")}
                      </th>
                      <th
                        onClick={() => handleSort("InstructorName")}
                        className="w-[10%] py-4 px-3 cursor-pointer hover:bg-slate-200 transition-colors"
                      >
                        Instructor {renderSortIndicator("InstructorName")}
                      </th>
                      <th className="w-[8%] py-4 px-3 text-center border-l border-slate-200/40 cursor-pointer hover:bg-slate-200 transition-colors">Status</th>
                      <th className="w-[8%] py-4 px-3 text-center border-l border-slate-200/40">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedData.map((ele, idx) => {
                      const totalQ = ele.rightAns + ele.wrongAns;
                      const pct = totalQ > 0 ? Math.round((ele.rightAns / totalQ) * 100) : 0;
                      const passed = pct >= 70;
                      const colors = getScoreColors(pct);
                      const displayIndex = (currentPage - 1) * itemsPerPage + idx + 1;

                      return (
                        <tr
                          key={ele.id || idx}
                          className="hover:bg-slate-50/40 transition-colors group"
                        >
                          <td className="py-4 px-3 text-center text-sm font-semibold text-slate-400">
                            {displayIndex}
                          </td>
                          <td className="py-4 px-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-600 font-bold text-xs border border-slate-200/60 shadow-sm shrink-0">
                                {ele.StudentName?.charAt(0) || "S"}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-slate-800 truncate" title={ele.StudentName || "Student"}>
                                  {ele.StudentName || "Student"}
                                </p>
                                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider truncate">
                                  ID: {ele.userId || "—"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-3">
                            <span className="text-sm font-semibold text-slate-600 truncate block w-full" title={ele.CourseName}>
                              {ele.CourseName || "General Quiz"}
                            </span>
                          </td>
                          <td className="py-4 px-3">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Target size={14} className="text-slate-500 shrink-0" />
                              <span className="text-sm font-bold text-slate-800 truncate block w-full" title={ele.name}>
                                {ele.name || "Untitled Quiz"}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-3">
                            <div className="w-full">
                              <div className="flex justify-between items-center mb-1">
                                <span className={`text-sm font-black ${colors.text}`}>{pct}%</span>
                                <span className="text-[10px] text-slate-400 font-bold whitespace-nowrap">
                                  {ele.rightAns}/{totalQ} right
                                </span>
                              </div>
                              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden border border-slate-200/20">
                                <div
                                  className={`h-full rounded-full ${colors.bg} transition-all duration-500`}
                                  style={{ width: `${pct}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-3">
                            <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 whitespace-nowrap">
                              <Calendar size={13} className="text-slate-400 shrink-0" />
                              {ele.createdOn ? moment(ele.createdOn).format("MMM DD, YYYY") : "—"}
                            </div>
                          </td>
                          <td className="py-4 px-3 text-sm font-semibold text-slate-600">
                            <div className="truncate" title={ele.InstructorName || "—"}>
                              {ele.InstructorName || "—"}
                            </div>
                          </td>
                          <td className="py-4 px-3 text-center border-l border-slate-200/40">
                            {passed ? (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider rounded-lg border ${colors.badge} whitespace-nowrap`}>
                                <CheckCircle2 size={10} className="text-emerald-600" />
                                Passed
                              </span>
                            ) : (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider rounded-lg border ${colors.badge} whitespace-nowrap`}>
                                <XCircle size={10} className="text-rose-600" />
                                Failed
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-3 text-center border-l border-slate-200/40">
                            <button
                              onClick={() => handleOpenReview(ele.id)}
                              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-800 hover:text-white text-slate-700 rounded-xl text-[11px] font-bold transition-all shadow-sm active:scale-95 whitespace-nowrap"
                            >
                              Review Answers
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Controls Section */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <div className="bg-white p-2 rounded-2xl shadow-sm border border-slate-200/60">
                <Pagination
                  className="pageNumbers-sudents"
                  currentPage={currentPage}
                  totalCount={10}
                  pageSize={totalPages}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            </div>
          )}

          {/* Exam Review Side Drawer */}
          <Modal isOpen={isReviewModalOpen} onClose={handleCloseReview} width="650px">
            {loadingReview ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-slate-800"></div>
                <p className="text-slate-500 font-bold text-sm mt-4">Loading exam details...</p>
              </div>
            ) : reviewError ? (
              <div className="text-center py-10 px-4">
                <XCircle className="text-rose-500 w-12 h-12 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-800">Review Error</h3>
                <p className="text-slate-500 text-sm mt-2">{reviewError}</p>
                <button
                  onClick={() => handleOpenReview(selectedTestId)}
                  className="mt-4 px-4 py-2 bg-slate-800 text-white rounded-xl text-xs font-bold"
                >
                  Retry
                </button>
              </div>
            ) : reviewData ? (
              <div className="space-y-6 pt-4 text-left">
                {/* Modal Header Card */}
                <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-5 relative overflow-hidden shadow-sm">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-slate-100 rounded-full -mr-8 -mt-8 opacity-40 pointer-events-none"></div>
                  
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-200 text-slate-800">
                        Exam Review
                      </span>
                      <h3 className="text-xl font-black text-slate-800 mt-2 leading-tight">
                        {reviewData.name || "Untitled Quiz"}
                      </h3>
                      <p className="text-slate-500 text-xs font-semibold mt-1">
                        Course: {reviewData.CourseName || "General Quiz"}
                      </p>
                    </div>
                    
                    {/* Score Circle or Badge */}
                    <div className="text-right">
                      <div className="inline-flex flex-col items-center justify-center bg-white border border-slate-200/80 rounded-2xl p-3 shadow-sm min-w-[70px]">
                        <span className="text-xl font-black text-slate-800">
                          {Math.round((reviewData.rightAns / (reviewData.rightAns + reviewData.wrongAns || 1)) * 100)}%
                        </span>
                        <span className="text-[9px] text-slate-400 font-extrabold uppercase mt-0.5">Score</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-5 border-t border-slate-200/60 pt-4">
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold tracking-wider uppercase">Student Name</p>
                      <p className="text-sm font-bold text-slate-700 mt-0.5">{reviewData.StudentName}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold tracking-wider uppercase">Attempted On</p>
                      <p className="text-sm font-bold text-slate-700 mt-0.5">
                        {reviewData.createdOn ? moment(reviewData.createdOn).format("MMM DD, YYYY hh:mm A") : "—"}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 mt-4 bg-white/60 p-2.5 rounded-xl border border-slate-200/40 text-xs">
                    <div className="flex items-center gap-1.5 font-semibold text-emerald-700">
                      <CheckCircle2 size={14} className="text-emerald-600" />
                      <span>{reviewData.rightAns} Correct</span>
                    </div>
                    <div className="flex items-center gap-1.5 font-semibold text-rose-700">
                      <XCircle size={14} className="text-rose-600" />
                      <span>{reviewData.wrongAns} Incorrect</span>
                    </div>
                    <div className="flex items-center gap-1.5 font-semibold text-slate-600 ml-auto">
                      <Target size={14} className="text-slate-500" />
                      <span>Total {reviewData.rightAns + reviewData.wrongAns} Qs</span>
                    </div>
                  </div>
                </div>

                {/* Questions list */}
                <div className="space-y-5">
                  <h4 className="text-sm font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                    <BookOpen size={16} className="text-slate-500" />
                    Questions Breakdown
                  </h4>

                  {reviewData.questions && reviewData.questions.length > 0 ? (
                    reviewData.questions.map((q, idx) => {
                      const hasIncorrectSelected = q.options.some(opt => opt.isSelected && !opt.isRight);
                      const missedCorrect = q.options.some(opt => !opt.isSelected && opt.isRight);
                      const isCorrectAnswered = !hasIncorrectSelected && !missedCorrect;

                      return (
                        <div
                          key={q.id || idx}
                          className={`border rounded-2xl p-5 transition-all shadow-sm ${
                            isCorrectAnswered
                              ? "bg-emerald-50/10 border-emerald-100 hover:border-emerald-200"
                              : "bg-rose-50/10 border-rose-100 hover:border-rose-200"
                          }`}
                        >
                          {/* Question Header */}
                          <div className="flex items-start justify-between gap-3 mb-4">
                            <div className="flex gap-2">
                              <span className="text-xs font-black text-slate-400 mt-0.5">
                                Q{idx + 1}.
                              </span>
                              <p className="text-sm font-extrabold text-slate-800 leading-relaxed">
                                {q.question}
                              </p>
                            </div>
                            <div>
                              {isCorrectAnswered ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-700">
                                  <CheckCircle2 size={10} /> Correct
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-rose-50 border border-rose-200 text-rose-700">
                                  <XCircle size={10} /> Incorrect
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Options List */}
                          <div className="space-y-2.5">
                            {q.options && q.options.map((opt, oIdx) => {
                              const isSelected = opt.isSelected;
                              const isRight = opt.isRight;

                              let optionStyle = "border-slate-200 bg-white text-slate-700 hover:bg-slate-50/50";
                              let badgeText = "";
                              let badgeStyle = "";

                              if (isSelected && isRight) {
                                optionStyle = "border-emerald-500 bg-emerald-50 text-emerald-900";
                                badgeText = "Your Correct Choice";
                                badgeStyle = "bg-emerald-500 text-white";
                              } else if (isSelected && !isRight) {
                                optionStyle = "border-rose-500 bg-rose-50 text-rose-900";
                                badgeText = "Your Incorrect Choice";
                                badgeStyle = "bg-rose-500 text-white";
                              } else if (!isSelected && isRight) {
                                optionStyle = "border-dashed border-emerald-500 bg-emerald-50/30 text-emerald-800";
                                badgeText = "Correct Answer";
                                badgeStyle = "bg-emerald-100 text-emerald-800 border border-emerald-200/50";
                              }

                              const label = String.fromCharCode(65 + oIdx);

                              return (
                                <div
                                  key={opt.id || oIdx}
                                  className={`flex items-center justify-between border rounded-xl p-3 text-xs font-semibold transition-all relative ${optionStyle}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black border ${
                                      isSelected
                                        ? isRight ? "bg-emerald-600 border-emerald-600 text-white" : "bg-rose-600 border-rose-600 text-white"
                                        : isRight ? "bg-emerald-100 border-emerald-500 text-emerald-700" : "bg-slate-100 border-slate-200 text-slate-500"
                                    }`}>
                                      {label}
                                    </span>
                                    <span className="text-slate-700 font-medium">{opt.answer}</span>
                                  </div>
                                  
                                  {badgeText && (
                                    <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider ${badgeStyle}`}>
                                      {badgeText}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-slate-400 text-sm font-semibold text-center py-6">No questions found for this quiz.</p>
                  )}
                </div>
              </div>
            ) : null}
          </Modal>

          {/* Backdrop Blur Overlay */}
          {isReviewModalOpen && (
            <div 
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 z-[999]" 
              onClick={handleCloseReview}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AdminQuizResults;
